import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppId, AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth, postWithAuth, putWithAuth } from "../../http";

export interface AppUser {
    id: string
    userName: string
    firstName: string
    surname: string
    email: string
    phone: string
    role: string
    position: string
    departmentName: string
    companyId: AppId
    status: string
    lockedBy: string
    lockReason: string
    lockedAt: Date | null
    expiryDate: Date | null
    createDate: Date
    companyRoleId?: string | null
}

export type AppUserWithoutLock = Omit<AppUser, "lockedBy" | "lockReason" | "lockedAt">
export type AppUserUpdate = AppUserWithoutLock & { changeReason?: string }

export type DraftAppUserWithoutLock = Omit<AppUser, "id" | "departmentName" | "lockedBy" | "lockReason" | "lockedAt" | "expiryDate" | "createDate">

export interface AppUserEmailType {
    email: string
}

export interface AppUserEmailWithPasswordType {
    email: string
    password: string
}

export interface UserBlockType {
    email: string,
    lockreason: string
}

export interface UserArchiveType {
    email: string,
    reason: string
    password: string
}

export interface UserPersonalData {
    firstName: string
    surname: string
    position: string
    password: string
}

export type DraftUser = Omit<AppUser, "id">

export const loadAppUsers = createAsyncThunk<AppUser[], void, AppThunkAPIType>("users-admin/load", async (_, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: "api/users" }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        return payload as AppUser[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadArchivedAppUsers = createAsyncThunk<AppUser[], void, AppThunkAPIType>(
    "archived-users-admin/load",
    async (_, { dispatch, rejectWithValue, requestId, getState }) => {
        const result = await dispatch(getWithAuth({ url: "api/users/archived" }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as AppUser[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadAppUsersForCompany = createAsyncThunk<AppUser[], AppId, AppThunkAPIType>(
    "users-admin-for-company/load",
    async (companyId, { dispatch, rejectWithValue }) => {

        const result = await dispatch(getWithAuth({ url: `api/users/for-company?companyId=${companyId}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as AppUser[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadAppUser = createAsyncThunk<AppUser, AppId, AppThunkAPIType>(
    "user/load",
    async (id, { dispatch, rejectWithValue, requestId, getState }) => {
        const result = await dispatch(getWithAuth({ url: `api/users/${id}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as AppUser
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const createAppUser = createAsyncThunk<AppUser, DraftAppUserWithoutLock, AppThunkAPIType>(
    "users-admin/create", 
    async (user: DraftAppUserWithoutLock, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/create",
            payload: user,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppUser
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateAppUser = createAsyncThunk<AppUser, AppUserUpdate, AppThunkAPIType>(
    "users-admin/update", 
    async (user: AppUserWithoutLock, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ 
            url: "api/users/update",
            payload: user,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as AppUser
            
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const unlockAppUser = createAsyncThunk<AppUser, AppUserEmailType, AppThunkAPIType>(
    "users-admin/unlock", 
    async (userEmail: AppUserEmailType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/unlock-user",
            payload: userEmail,
        }))
       
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppUser;
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const blockAppUser = createAsyncThunk<AppUser, UserBlockType, AppThunkAPIType>(
    "users-admin/block-user", 
    async ( userBlock: UserBlockType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/block-user",
            payload: userBlock,
        }))
       
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppUser;
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const archiveAppUser = createAsyncThunk<AppUser, UserArchiveType, AppThunkAPIType>(
    "users-admin/archive-user", 
    async (userArchive: UserArchiveType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/archive-user",
            payload: userArchive,
        }))
       
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppUser;
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const unarchiveAppUser = createAsyncThunk<AppUser, UserArchiveType, AppThunkAPIType>(
    "users-admin/unarchive-user", 
    async (userUnarchive: UserArchiveType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/unarchive-user",
            payload: userUnarchive,
        }))
       
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppUser;
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

//! Tutaj dopisałem Func, bo deleteAppUser istnieje - do poprawki

export const deleteAppUserFunc = createAsyncThunk<AppUser, AppUserEmailWithPasswordType, AppThunkAPIType>(
    "users-admin/delete-user", 
    async ( userEmailWithPassword: AppUserEmailWithPasswordType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/delete-user",
            payload: userEmailWithPassword,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppUser;
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const resetAppUserPassword = createAsyncThunk<void, AppUserEmailType, AppThunkAPIType>(
    "users-admin/reset-user-password", 
    async (userEmail: AppUserEmailType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/reset-user-password",
            payload: userEmail,
        }))
       
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return;
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const sendAppUserInvitation = createAsyncThunk<AppId, AppUserEmailType, AppThunkAPIType>(
    "users-admin/sendAppUserInvitation", 
    async (userEmail: AppUserEmailType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ 
            url: "api/users/send-invitation",
            payload: userEmail
        }))
       
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return "";
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateUserPersonalData = createAsyncThunk<AppUser, UserPersonalData, AppThunkAPIType>(
    "users/update-personal-data", 
    async (user: UserPersonalData, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ 
            url: "api/users/personal-data",
            payload: user,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as AppUser
            
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)